import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { mainClient, getWebSocketReady } from "../apollo";
import {
  SUBSCRIBE_REQUEST_BANKING,
} from "../components/graphql/banking";
import {
  REQUEST_REGISTER,
} from "../components/graphql/monitor";
import {
  SUBSCRIBE_ABUSE,
} from "../components/graphql/abuse";
import {
  SUBSCRIBE_CURRENTSTATE,
  SUBSCRIBE_USER_SETTING,
} from "../components/graphql/manageUser";
import {
  SUBSCRIBE_REAL_SIGN,
  SUBSCRIBE_REAL_HOGA,
} from "../components/graphql/itemInfo";
import { handleUserCurrentState } from "../components/cache/userData";
import { saveSignData, saveHogaData } from "../components/cache/itemData";
import { subscriptionEvents } from "../utils/subscriptionEvents";
import { SUBSCRIBE_NEW_CUSTOMER_INQUIRIES } from "../components/graphql/customerInquiry";
import {
  SUBSCRIBE_PROFIT_ALERT,
} from "../components/graphql/profitAlert";
import audioManager from "../utils/audioManager";

// 개별 Context 분리 - 각 데이터 변경 시 해당 소비자만 재렌더링
const RegisterDataContext = createContext(null);
const BankingDataContext = createContext(null);
const AbuseDataContext = createContext(null);
const LatestAbuseAtContext = createContext(null);
const ProfitAlertDataContext = createContext(null);
const LatestProfitAlertAtContext = createContext(null);

/**
 * SubscriptionProvider
 *
 * 모든 GraphQL 구독을 중앙에서 관리하는 Provider 컴포넌트
 * 중복 구독을 방지하고 구독 데이터를 개별 Context를 통해 배포합니다.
 *
 * 관리하는 구독:
 * 1. REQUEST_REGISTER - 회원가입 요청 구독
 * 2. SUBSCRIBE_REQUEST_BANKING - 입출금 요청 구독
 * 3. SUBSCRIBE_ABUSE - 이상거래 감지 구독
 * 4. SUBSCRIBE_CURRENTSTATE - 사용자 상태 변경 구독 (전역)
 * 5. SUBSCRIBE_REAL_SIGN - 체결 데이터 구독 (DashBoard에서 이동)
 * 6. SUBSCRIBE_REAL_HOGA - 호가 데이터 구독 (DashBoard에서 이동)
 * 7. SUBSCRIBE_USER_SETTING - 유저 설정 변경 구독 (DashBoard에서 이동)
 * 8. SUBSCRIBE_NEW_CUSTOMER_INQUIRIES - 고객 문의 구독 (새 문의/추가 질문 알림)
 * 9. SUBSCRIBE_PROFIT_ALERT - 수익 알림 구독 (일일 실손익 임계값 돌파)
 */
export const SubscriptionProvider = ({ children }) => {
  const [registerData, setRegisterData] = useState([]);
  const [bankingData, setBankingData] = useState([]);
  const [abuseData, setAbuseData] = useState(null);
  const [latestAbuseAt, setLatestAbuseAt] = useState(null);
  const [profitAlertData, setProfitAlertData] = useState(null);
  const [latestProfitAlertAt, setLatestProfitAlertAt] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let registerSubscription = null;
    let bankingSubscription = null;
    let abuseSubscription = null;
    let currentStateSubscription = null;
    let signSubscription = null;
    let hogaSubscription = null;
    let userSettingSubscription = null;
    let inquirySubscription = null;
    let profitAlertSubscription = null;

    const setupSubscriptions = async () => {
      console.log("🔔 [SubscriptionContext] Provider mounted - WebSocket 준비 대기 중...");

      await getWebSocketReady();

      if (!isMounted) {
        console.log("🔔 [SubscriptionContext] 컴포넌트 unmount됨 - subscription 설정 취소");
        return;
      }

      console.log("🔔 [SubscriptionContext] WebSocket 준비 완료 - subscription 설정 시작");

      // 1. 회원가입 요청 구독
      registerSubscription = mainClient
        .subscribe({
          query: REQUEST_REGISTER,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const requestRegister = result?.data?.requestRegister;
            if (requestRegister !== undefined) {
              setRegisterData(requestRegister ?? []);
            }
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] REQUEST_REGISTER error:", error);
          },
        });

      // 2. 입출금 요청 구독
      bankingSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_REQUEST_BANKING,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const subscribeRequestBanking = result?.data?.subscribeRequestBanking;
            if (subscribeRequestBanking !== undefined) {
              setBankingData(subscribeRequestBanking ?? []);
              subscriptionEvents.emit('banking', subscribeRequestBanking);
            }
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_REQUEST_BANKING error:", error);
          },
        });

      // 3. 이상거래 감지 구독
      abuseSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_ABUSE,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const subscribeAbuse = result?.data?.subscribeAbuse;
            if (subscribeAbuse) {
              setAbuseData(subscribeAbuse);
              setLatestAbuseAt(Date.now());
            }
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_ABUSE error:", error);
          },
        });

      // 4. 사용자 상태 변경 구독 (전역 - 캐시 업데이트 + 이벤트 발행)
      currentStateSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_CURRENTSTATE,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const data = result?.data?.subscribeCurrentState;
            if (data) {
              handleUserCurrentState(data);
              subscriptionEvents.emit('currentState', data);
            }
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_CURRENTSTATE error:", error);
          },
        });

      // 5. 체결 데이터 구독 (DashBoard에서 이동 - 캐시 업데이트 + 이벤트 발행)
      signSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_REAL_SIGN,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const signs = result?.data?.subscribeRealSign;
            if (!signs) return;

            if (Array.isArray(signs)) {
              const latestSignsByCode = new Map();
              signs.forEach(sign => {
                if (sign && sign.code) {
                  latestSignsByCode.set(sign.code, sign);
                }
              });
              latestSignsByCode.forEach(sign => {
                saveSignData(sign);
              });
            } else if (signs.code) {
              saveSignData(signs);
            }

            subscriptionEvents.emit('sign', signs);
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_REAL_SIGN error:", error);
          },
        });

      // 6. 호가 데이터 구독 (DashBoard에서 이동 - 캐시만 업데이트, 이벤트 불필요)
      hogaSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_REAL_HOGA,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const data = result?.data?.subscribeReal;
            if (!data) return;
            saveHogaData(data);
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_REAL_HOGA error:", error);
          },
        });

      // 7. 유저 설정 변경 구독 (DashBoard에서 이동 - 캐시 업데이트 + 이벤트 발행)
      userSettingSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_USER_SETTING,
        })
        .subscribe({
          next: (result) => {
            if (result.errors) console.log("subscribeUserSetting errors:", result.errors);
            const data = result?.data?.subscribeUserSetting;
            if (!data) return;
            handleUserCurrentState(data);
            subscriptionEvents.emit('userSetting', data);
          },
          error: (error) => {
            console.error("❌ [SubscriptionContext] SUBSCRIBE_USER_SETTING error:", error);
          },
        });

      // 8. 고객 문의 구독 (새 문의/추가 질문 알림)
      inquirySubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_NEW_CUSTOMER_INQUIRIES,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: ({ data }) => {
            const event = data?.subscribeNewInquiries;
            if (event) {
              const { inquiry } = event;
              audioManager.play("newMessage");
              subscriptionEvents.emit("newInquiry", inquiry);
            }
          },
          error: (err) => console.error("❌ [SubscriptionContext] 문의 구독 오류:", err),
        });

      // 9. 수익 알림 구독
      profitAlertSubscription = mainClient
        .subscribe({
          query: SUBSCRIBE_PROFIT_ALERT,
          fetchPolicy: "no-cache",
        })
        .subscribe({
          next: (result) => {
            const item = result?.data?.subscribeProfitAlert;
            if (!item) return;
            setProfitAlertData(item);
            const ts = item.triggeredAt
              ? new Date(item.triggeredAt).getTime()
              : Date.now();
            setLatestProfitAlertAt(Number.isFinite(ts) ? ts : Date.now());
            subscriptionEvents.emit("profitAlert", item);
          },
          error: (error) => {
            console.error(
              "❌ [SubscriptionContext] SUBSCRIBE_PROFIT_ALERT error:",
              error,
            );
          },
        });
    };

    setupSubscriptions();

    return () => {
      isMounted = false;
      console.log("🔕 [SubscriptionContext] Provider unmounting - cleaning up subscriptions");
      if (registerSubscription) registerSubscription.unsubscribe();
      if (bankingSubscription) bankingSubscription.unsubscribe();
      if (abuseSubscription) abuseSubscription.unsubscribe();
      if (currentStateSubscription) currentStateSubscription.unsubscribe();
      if (signSubscription) signSubscription.unsubscribe();
      if (hogaSubscription) hogaSubscription.unsubscribe();
      if (userSettingSubscription) userSettingSubscription.unsubscribe();
      if (inquirySubscription) inquirySubscription.unsubscribe();
      if (profitAlertSubscription) profitAlertSubscription.unsubscribe();
    };
  }, []);

  // 개별 Context value를 useMemo로 메모이제이션
  const registerValue = useMemo(() => registerData, [registerData]);
  const bankingValue = useMemo(() => bankingData, [bankingData]);
  const abuseValue = useMemo(() => abuseData, [abuseData]);
  const latestAbuseAtValue = useMemo(() => latestAbuseAt, [latestAbuseAt]);
  const profitAlertValue = useMemo(() => profitAlertData, [profitAlertData]);
  const latestProfitAlertAtValue = useMemo(() => latestProfitAlertAt, [latestProfitAlertAt]);

  return (
    <RegisterDataContext.Provider value={registerValue}>
      <BankingDataContext.Provider value={bankingValue}>
        <AbuseDataContext.Provider value={abuseValue}>
          <LatestAbuseAtContext.Provider value={latestAbuseAtValue}>
            <ProfitAlertDataContext.Provider value={profitAlertValue}>
              <LatestProfitAlertAtContext.Provider value={latestProfitAlertAtValue}>
                {children}
              </LatestProfitAlertAtContext.Provider>
            </ProfitAlertDataContext.Provider>
          </LatestAbuseAtContext.Provider>
        </AbuseDataContext.Provider>
      </BankingDataContext.Provider>
    </RegisterDataContext.Provider>
  );
};

/**
 * useRegisterData
 * 회원가입 요청 데이터 - registerData 변경 시에만 재렌더링
 */
export const useRegisterData = () => {
  return useContext(RegisterDataContext);
};

/**
 * useBankingData
 * 입출금 요청 데이터 - bankingData 변경 시에만 재렌더링
 */
export const useBankingData = () => {
  return useContext(BankingDataContext);
};

/**
 * useAbuseData
 * 이상거래 감지 데이터 - abuseData 변경 시에만 재렌더링
 */
export const useAbuseData = () => {
  const context = useContext(AbuseDataContext);
  return context;
};

/**
 * useLatestAbuseAt
 * 이상거래 감지 subscription이 마지막으로 수신된 ms epoch. NEW 판정용.
 */
export const useLatestAbuseAt = () => {
  return useContext(LatestAbuseAtContext);
};

/**
 * useProfitAlertData
 * 수익 알림 subscription의 마지막 item. 페이지 훅에서 관찰용.
 */
export const useProfitAlertData = () => {
  return useContext(ProfitAlertDataContext);
};

/**
 * useLatestProfitAlertAt
 * 수익 알림 subscription이 마지막으로 수신된 ms epoch. NEW 판정용.
 */
export const useLatestProfitAlertAt = () => {
  return useContext(LatestProfitAlertAtContext);
};
